import React, { useState } from "react";
import { RotateCcw, Plus, AlertTriangle, ShieldCheck } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { Modal } from "../../components/common/Modal";
import { useNotification } from "../../app/context/NotificationContext";

export function ReturnModal({ isOpen, onClose }) {
  const { products, addReturn } = useApp();
  const { addToast } = useNotification();

  const [type, setType] = useState("Customer → Shop Return");
  const [productId, setProductId] = useState(products[0]?.id || "");
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("Package seal damaged in transit");
  const [actionTaken, setActionTaken] = useState("Written off to Damaged pool & replaced.");

  const selectedProduct = products.find((p) => p.id === productId);
  const financialImpact = (selectedProduct?.purchasePrice || 100) * qty;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addReturn({
        type,
        productId,
        productName: selectedProduct?.name || "Product",
        qty: Number(qty),
        reason,
        actionTaken,
        financialImpact: -financialImpact,
        status: "Processed"
      });
      addToast(`Return/Damage log recorded for ${selectedProduct?.name}!`, "success");
      onClose();
    } catch (err) {
      addToast("Failed to record return.", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Log Return, Damage or Rejection"
      subtitle="Account for spoiled packets, supplier rejections, and inventory write-offs"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Return / Incident Channel *
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          >
            <option value="Customer → Shop Return">Customer → Shop Return (Refund/Replace)</option>
            <option value="Shop → Supplier Claim">Shop → Supplier Claim (Credit Note)</option>
            <option value="Warehouse Handling Damage">Warehouse Handling Damage (Forklift / Leakage)</option>
          </select>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Product Item *
          </label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          >
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Quantity *
            </label>
            <input
              type="number"
              min="1"
              required
              value={qty}
              onChange={(e) => setQty(Number(e.target.value))}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono font-bold"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Financial Impact (₹)
            </label>
            <div className="px-3 py-2 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-xl font-mono font-bold text-rose-600 dark:text-rose-400">
              -{formatCurrency(financialImpact)}
            </div>
          </div>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Reason for Return / Damage *
          </label>
          <input
            type="text"
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Expired lot, puncture, customer return"
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Action Taken & Resolution
          </label>
          <input
            type="text"
            value={actionTaken}
            onChange={(e) => setActionTaken(e.target.value)}
            placeholder="e.g. Stock quarantined, credit note requested"
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          />
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white font-semibold rounded-xl shadow-lg shadow-rose-600/30"
          >
            Log Return & Update Stock
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ReturnsModule() {
  const { returns } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const columns = [
    {
      header: "Incident ID",
      accessorKey: "id",
      render: (val, row) => (
        <div>
          <span className="font-bold font-mono text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400 font-mono">Date: {formatDate(row.date)}</p>
        </div>
      )
    },
    {
      header: "Return Channel",
      accessorKey: "type",
      render: (val) => <Badge variant="rose">{val}</Badge>
    },
    {
      header: "Product SKU",
      accessorKey: "productName",
      render: (val, row) => (
        <div>
          <span className="font-bold text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400 font-mono">Quantity: {row.qty} units</p>
        </div>
      )
    },
    {
      header: "Reason & Action",
      accessorKey: "reason",
      render: (val, row) => (
        <div>
          <p className="text-slate-800 dark:text-slate-200 font-medium">{val}</p>
          <p className="text-[10px] text-slate-400">{row.actionTaken}</p>
        </div>
      )
    },
    {
      header: "Financial Impact",
      accessorKey: "financialImpact",
      align: "right",
      render: (val) => (
        <span className="font-mono font-bold text-rose-600 dark:text-rose-400">
          {formatCurrency(val)}
        </span>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-emerald-500" />
            Returns, Damages & Rejection Registry
          </h2>
          <p className="text-xs text-slate-500">
            Audit trail of customer returns, supplier credit note claims, handling damages, and financial impact
          </p>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-rose-600/30 transition"
        >
          <Plus className="w-4 h-4" />
          Log Return / Damage
        </button>
      </div>

      <DataTable
        columns={columns}
        data={returns}
        title="Returns & Damages Register"
        subtitle="Manage inventory status transitions and write-off ledgers"
        filename="kirana_returns_damages"
      />

      <ReturnModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
